
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PuttSituation } from "@/types/golf";
import FavoriteButton from "@/components/common/FavoriteButton";
import { useTranslation } from "react-i18next";

interface PuttingSituationCardProps {
  situation: PuttSituation;
  index: number;
  onSelect: (id: number) => void;
  refreshAfterToast?: () => void;
}

const PuttingSituationCard = ({ situation, index, onSelect, refreshAfterToast }: PuttingSituationCardProps) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tTraining } = useTranslation('training');
  
  // Map situation ID to translation key
  const situationKeys: Record<number, string> = {
    1: 'puttingTraining.exercises.longPutts',
    2: 'puttingTraining.exercises.mediumPutts',
    3: 'puttingTraining.exercises.slopePutts',
    4: 'puttingTraining.exercises.offensivePutts',
    5: 'puttingTraining.exercises.shortPutts1m',
    6: 'puttingTraining.exercises.shortPutts2m',
  };

  const translationKey = situationKeys[situation.id];
  const translatedName = translationKey ? tCommon(`${translationKey}.name`) : situation.name;
  const translatedDescription = translationKey ? tCommon(`${translationKey}.description`) : situation.description;
  
  // Fix the pluralization in condition texts
  const updatedSituation = {
    ...situation,
    scoring: situation.scoring.map(score => ({
      ...score,
      condition: score.condition
        .replace("(1pts)", "(1pt)")
        .replace("(0pts)", "(0pt)")
    }))
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card className="h-full hover:shadow-lg transition-shadow duration-300">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-golf-700">{translatedName}</CardTitle>
            <FavoriteButton 
              testId={updatedSituation.id.toString()} 
              testType="putting"
              refreshAfterToast={refreshAfterToast}
            />
          </div>
          <CardDescription className="text-center">
            {translatedDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <img
            src={updatedSituation.image}
            alt={updatedSituation.name}
            className="w-full h-48 object-cover rounded-md mb-4"
          />
          <Button
            onClick={() => onSelect(updatedSituation.id)}
            className="w-full bg-golf-600 hover:bg-golf-700 text-white"
          >
            {tTraining('putting.startTest')}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PuttingSituationCard;
